import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Chart, registerables } from 'chart.js';
import { CarteraService } from 'src/app/services/cartera/cartera.service';
import { UserService } from 'src/app/services/user/user.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';
import { claseMarca, fechaLiq, puedeVerLiquidacion } from 'src/app/services/liquidacion/marcas';

Chart.register(...registerables);

type Tab = 'cuotas' | 'recargos' | 'pagos' | 'estadisticas' | 'bloqueos';

@Component({
  selector: 'app-cliente-detalle',
  templateUrl: './cliente-detalle.component.html',
  styleUrls: ['./cliente-detalle.component.css']
})
export class ClienteDetalleComponent implements OnInit, OnDestroy {
  code = '';
  data: any = null;
  loading = false;
  error: string | null = null;
  activeTab: Tab = 'cuotas';
  cedulaCliente: string | null = null;

  // Charts
  @ViewChild('comportChart') comportChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('feesPorMesChart') feesPorMesChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('diasPorMesChart') diasPorMesChartRef!: ElementRef<HTMLCanvasElement>;
  private comportChart?: Chart;
  private feesPorMesChart?: Chart;
  private diasPorMesChart?: Chart;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    public carteraSvc: CarteraService,
    private userSvc: UserService,
    private _auth: AuthService,
    private liq: LiquidacionService
  ) {}

  // EST-01/08 (Mario, 12-oct): la marca de liquidación al comercio de cada crédito; solo con liquidacion.consultar.
  get puedeLiq(): boolean { return puedeVerLiquidacion(this._auth); }
  marcas: Record<string, any> = {};
  claseMarca = claseMarca;
  fechaLiq = fechaLiq;
  marcaDe(c: any): any { return c && c.number != null ? this.marcas[String(c.number)] : null; }

  async cargarMarcas() {
    if (!this.puedeLiq || !this.data?.creditos?.length) return;
    try { this.marcas = await this.liq.marcasVentas(this.data.creditos.map((c: any) => c.number != null ? String(c.number) : null)); }
    catch { this.marcas = {}; }
  }

  async ngOnInit() {
    this.code = this.route.snapshot.params['code'];
    await this.load();
  }

  async load() {
    this.loading = true;
    this.error = null;
    try {
      const r = await this.carteraSvc.getCliente360(this.code);
      if (r?.data) {
        this.data = r.data;
        this.loadCedula();
        this.cargarMarcas();
      } else {
        this.error = 'Cliente no encontrado';
      }
    } catch (e: any) {
      this.error = e?.message || 'Error al cargar';
    } finally {
      this.loading = false;
    }
  }

  // Obtiene la cédula desde el endpoint /api/person/code, porque getCliente360
  // expone `person.identity` (campo legacy frecuentemente vacío). La cédula real
  // vive en la colección identities asociada al person. Mismo patrón que usa
  // contact-profile.getPerson() (resp.resp.identitys[0]).
  private async loadCedula() {
    try {
      const code = this.data?.person?.code;
      if (!code) return;
      const resp: any = await this.userSvc.getPersonbyCode({ code });
      const root = resp?.resp || resp;
      const ident = root?.identitys?.[0] || root?.person?.identitys?.[0];
      if (ident?.identity) {
        const codeLetter = ident?.code_identity?.name || 'V';
        this.cedulaCliente = `${codeLetter}-${ident.identity}`;
      }
    } catch (err) {
      console.warn('No se pudo cargar la cédula del cliente:', err);
    }
  }

  ngOnDestroy() {
    this.comportChart?.destroy();
    this.feesPorMesChart?.destroy();
    this.diasPorMesChart?.destroy();
  }

  setTab(t: Tab) {
    this.activeTab = t;
    if (t === 'estadisticas') setTimeout(() => this.renderCharts(), 100);
  }

  private renderCharts() {
    if (!this.data) return;
    this.renderComport();
    this.renderFeesPorMes();
    this.renderDiasPorMes();
  }

  private renderComport() {
    if (!this.comportChartRef) return;
    const s = this.data.cuotas.summary;
    if (this.comportChart) this.comportChart.destroy();
    this.comportChart = new Chart(this.comportChartRef.nativeElement, {
      type: 'doughnut',
      data: {
        labels: ['A tiempo', 'Tarde'],
        datasets: [{ data: [s.a_tiempo, s.tarde], backgroundColor: ['#3b82f6', '#1e3a8a'], borderWidth: 0 }]
      },
      options: {
        responsive: true, maintainAspectRatio: false, cutout: '65%',
        plugins: { legend: { position: 'bottom', labels: { font: { size: 11 } } } }
      }
    });
  }

  private renderFeesPorMes() {
    if (!this.feesPorMesChartRef || !this.data.fees.por_mes.length) return;
    const labels = this.data.fees.por_mes.map((m: any) => this.monthLabel(m.year, m.month));
    if (this.feesPorMesChart) this.feesPorMesChart.destroy();
    this.feesPorMesChart = new Chart(this.feesPorMesChartRef.nativeElement, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          { label: 'Recargos', data: this.data.fees.por_mes.map((m: any) => m.count), backgroundColor: '#3b82f6', yAxisID: 'y', borderRadius: 4 },
          { label: 'Monto $', data: this.data.fees.por_mes.map((m: any) => m.monto), backgroundColor: '#93c5fd', yAxisID: 'y1', borderRadius: 4 }
        ]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'top', labels: { font: { size: 11 } } } },
        scales: {
          y:  { position: 'left',  title: { display: true, text: 'Cantidad' }, ticks: { font: { size: 10 } } },
          y1: { position: 'right', title: { display: true, text: '$ Monto' }, ticks: { font: { size: 10 } }, grid: { drawOnChartArea: false } },
          x:  { ticks: { font: { size: 10 } } }
        }
      }
    });
  }

  private renderDiasPorMes() {
    if (!this.diasPorMesChartRef || !this.data.fees.por_mes.length) return;
    const labels = this.data.fees.por_mes.map((m: any) => this.monthLabel(m.year, m.month));
    if (this.diasPorMesChart) this.diasPorMesChart.destroy();
    this.diasPorMesChart = new Chart(this.diasPorMesChartRef.nativeElement, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: 'Días promedio de mora',
          data: this.data.fees.por_mes.map((m: any) => m.dias_promedio),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.3,
          pointBackgroundColor: '#1d4ed8',
          pointRadius: 4
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: { title: { display: true, text: 'Días promedio' }, ticks: { font: { size: 10 } }, beginAtZero: true },
          x: { ticks: { font: { size: 10 } } }
        }
      }
    });
  }
  goBack() { window.history.back(); }
  goToCartera() { this.router.navigate(['/console/cartera']); }

  fmtMoney(n: number | null | undefined): string {
    if (n == null) return '—';
    return '$' + n.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  fmtInt(n: number | null | undefined): string {
    if (n == null) return '—';
    return n.toLocaleString('es-VE');
  }
  fmtDate(s: any): string {
    if (!s) return '—';
    try { return new Date(s).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return String(s); }
  }
  fmtDateTime(s: any): string {
    if (!s) return '—';
    try { return new Date(s).toLocaleString('es-VE'); } catch { return String(s); }
  }
  fmtRelative(s: any): string {
    if (!s) return '—';
    const ms = Date.now() - new Date(s).getTime();
    if (ms < 60000) return Math.round(ms / 1000) + 's atrás';
    if (ms < 3600000) return Math.round(ms / 60000) + 'm atrás';
    if (ms < 86400000) return Math.round(ms / 3600000) + 'h atrás';
    return Math.round(ms / 86400000) + 'd atrás';
  }
  moraBadge(d: number): string {
    if (d <= 0) return 'mora-0';
    if (d <= 30) return 'mora-low';
    if (d <= 60) return 'mora-med';
    if (d <= 90) return 'mora-high';
    if (d <= 180) return 'mora-crit';
    return 'mora-extreme';
  }
  scoreBadge(): string {
    if (!this.data) return '';
    return 'score-' + this.data.estadisticas.score_color;
  }
  tendenciaIconClass(): string {
    const t = this.data?.estadisticas?.tendencia;
    // 🔴 DOS FORMAS DISTINTAS, no el mismo icono en dos colores. Al cambiar los
    // nombres de FontAwesome 6 por los de la 5 instalada (2026-09-20), las dos
    // flechas de tendencia —`fa-arrow-trend-up` y `fa-arrow-trend-down`— cayeron
    // en el mismo `fa-chart-line`, y el unico distintivo quedo siendo el COLOR.
    // Quien no distingue rojo de verde ve el mismo dibujo para «va mejorando» y
    // «va empeorando», y en una impresion en gris tambien. El color acompaña; la
    // forma es la que dice.
    if (t === 'mejorando') return 'fa-arrow-up text-green-600';
    if (t === 'empeorando') return 'fa-arrow-down text-red-600';
    if (t === 'estable') return 'fa-minus text-gray-500';
    return '';
  }
  tendenciaColor(): string {
    const t = this.data?.estadisticas?.tendencia;
    if (t === 'mejorando') return 'trend-good';
    if (t === 'empeorando') return 'trend-bad';
    return 'trend-neutral';
  }

  monthLabel(year: number, month: number): string {
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${meses[month - 1] || ''} ${year}`;
  }

  porConceptoEntries(): Array<{ concepto: string; pendientes: number; pendientes_monto: number; pagados: number; pagados_monto: number }> {
    if (!this.data?.fees?.por_concepto) return [];
    return Object.entries(this.data.fees.por_concepto).map(([k, v]: any) => ({ concepto: k, ...v }));
  }
  porCanalEntries(): Array<{ canal: string; count: number; monto: number }> {
    if (!this.data?.pagos?.por_canal) return [];
    return Object.entries(this.data.pagos.por_canal).map(([k, v]: any) => ({ canal: k, ...v }));
  }
  pagosRecientes(): Array<{ date: any; amount: number; payment_method_name?: string; payment_method_code?: string; source?: string }> {
    return (this.data?.pagos?.recientes || []).slice(0, 50);
  }

  // ============================================
  // HELPERS PARA TABLA CUOTAS EN MORA — DETALLE
  // ============================================
  montoPagado(c: any): number {
    const amount = Number(c?.amount) || 0;
    const due = Number(c?.dueBalance) || 0;
    return Math.max(0, +(amount - due).toFixed(2));
  }

  brokerLabel(feeDays: number | null | undefined): string {
    const d = Number(feeDays) || 0;
    if (d <= 0) return '—';
    if (d <= 45) return '4 - 45';
    if (d <= 60) return '46 - 60';
    return '> 60';
  }

  feeStatusLabel(fee: any): string {
    if (!fee) return '—';
    const pending = fee.payment_status_code === 'PaymentPending';
    const due = Number(fee.due_balance) || 0;
    if (!pending && due === 0) return 'Completado';
    if (pending && due === 0) return 'Completado';
    return 'Pendiente';
  }

  feeStatusClass(fee: any): { [k: string]: boolean } {
    const status = this.feeStatusLabel(fee);
    return {
      'cd-pill-good': status === 'Completado',
      'cd-pill-warn': status === 'Pendiente'
    };
  }

  feeCondicion(fee: any): string {
    // El backend actual no expone un flag de recalculado, por lo que mostramos 'Original'.
    // Si en el futuro se agrega `fee.recalculated`, esta función lo refleja sin tocar la tabla.
    if (!fee) return '—';
    return fee.recalculated ? 'Recalculado' : 'Original';
  }

  // Acciones
  actionLoading: string | null = null;
  actionMessage: string | null = null;

  async recalcCliente() {
    if (!this.data) return;
    this.actionLoading = 'recalc';
    this.actionMessage = null;
    try {
      await this.carteraSvc.recalcCliente(this.data.person._id);
      this.actionMessage = '✓ Mora recalculada';
      await this.load();
    } catch (e: any) {
      this.actionMessage = '✗ ' + (e?.error?.error || e?.message);
    } finally { this.actionLoading = null; }
  }

  async toggleBloqueo() {
    if (!this.data) return;
    const willBlock = this.data.person.quota_active;
    const reason = willBlock ? prompt('Motivo del bloqueo:') : null;
    if (willBlock && !reason) return;
    if (!confirm(`¿${willBlock ? 'Bloquear' : 'Desbloquear'}?`)) return;
    this.actionLoading = 'bloqueo';
    this.actionMessage = null;
    try {
      await this.carteraSvc.toggleBloqueoManual(this.data.person._id, willBlock, reason || undefined);
      this.actionMessage = willBlock ? '✓ Bloqueado' : '✓ Desbloqueado';
      await this.load();
    } catch (e: any) {
      this.actionMessage = '✗ ' + (e?.error?.error || e?.message);
    } finally { this.actionLoading = null; }
  }
}
